import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, Input, Result, Select, Space, Table, Tag } from "antd";
import { AppstoreOutlined, SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import { usePermissions } from "../../utils/usePermissions";
import { isSuperAdmin } from "../../utils/permissions";
import { BASE_API_URL } from "../../utils/consts";

interface TokenOption {
  _id: string;
  name: string;
  locationId?: { _id: string; name: string } | null;
}

interface Warehouse {
  Id: number;
  Name: string;
  IsActive: boolean;
  IsForSale: boolean;
  ParentId?: number;
}

interface ProductCategory {
  Id: number;
  Name: string;
  IsActive: boolean;
  IsForSale: boolean;
  ButtonBackColor?: number;
  ButtonForeColor?: number;
  BackColor?: string;
  ForeColor?: string;
  WarehouseId?: number;
  Warehouse?: Warehouse;
}

const THREE_STATE_OPTIONS = [
  { value: "", label: "Svi" },
  { value: "true", label: "Da" },
  { value: "false", label: "Ne" },
];

const normalize = (s: string) =>
  (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

// Octopus's BackColor/ForeColor fields aren't documented beyond "string", so we
// only trust them as CSS colors when they look like a hex code — anything else
// (e.g. a raw OLE integer) is ignored rather than risk a broken swatch.
const isCssHexColor = (v?: string) => !!v && /^#?[0-9a-fA-F]{6}$/.test(v);
const asCssColor = (v?: string) => (isCssHexColor(v) ? (v!.startsWith("#") ? v : `#${v}`) : undefined);

const OctopusCategories: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const rawClientId = userData?.clientId;
  const clientId =
    typeof rawClientId === "object" ? rawClientId?._id : rawClientId;

  const { hasPermission } = usePermissions();
  const canView =
    isSuperAdmin(userData?.roles) || hasPermission("octopus-categories:view");

  const [tokens, setTokens] = useState<TokenOption[]>([]);
  const [selectedTokenId, setSelectedTokenId] = useState<string | undefined>();
  const [tokensLoading, setTokensLoading] = useState(false);

  const [keyword, setKeyword] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [forSaleFilter, setForSaleFilter] = useState("");

  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [total, setTotal] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({ columnKey: "Name", order: "ascend" });

  React.useEffect(() => {
    if (!clientId || !canView) return;
    setTokensLoading(true);
    axios
      .get(`${BASE_API_URL}/${clientId}/octopus-tokens`)
      .then((res) => {
        setTokens(res.data || []);
        if (res.data?.length === 1) setSelectedTokenId(res.data[0]._id);
      })
      .catch(() => {})
      .finally(() => setTokensLoading(false));
  }, [clientId]);

  const currentLocationId = () => {
    const token = tokens.find((tk) => tk._id === selectedTokenId);
    return token?.locationId?._id;
  };

  const resetTable = () => {
    setFilteredInfo({});
    setSortedInfo({ columnKey: "Name", order: "ascend" });
    setSearchText("");
  };

  // "Pretraži" — server-side keyword/active/isForSale search via /ProductCategory/Search.
  // Uses a large page size so a typical category catalog comes back in one call
  // while still exercising the vendor's documented search endpoint.
  const handleSearch = async () => {
    setLoading(true);
    setSearched(true);
    resetTable();
    try {
      const params: Record<string, string> = { pageNumber: "1", pageSize: "500" };
      if (keyword.trim()) params.keyword = keyword.trim();
      if (activeFilter) params.active = activeFilter;
      if (forSaleFilter) params.isForSale = forSaleFilter;
      const locationId = currentLocationId();
      if (locationId) params.locationId = locationId;

      const res = await axios.get(
        `${BASE_API_URL}/${clientId}/octopus-tokens/product-categories/search`,
        { params }
      );
      setCategories(Array.isArray(res.data?.items) ? res.data.items : []);
      setTotal(res.data?.total);
    } catch (err: any) {
      setCategories([]);
      setTotal(undefined);
    } finally {
      setLoading(false);
    }
  };

  // "Prikaži sve" — plain unpaginated /ProductCategory list, ignoring keyword/active.
  // Handy for a clean full reset when the search filters get in the way.
  const handleShowAll = async () => {
    setLoading(true);
    setSearched(true);
    resetTable();
    try {
      const params: Record<string, string> = {};
      if (forSaleFilter) params.isForSale = forSaleFilter;
      const locationId = currentLocationId();
      if (locationId) params.locationId = locationId;

      const res = await axios.get(`${BASE_API_URL}/${clientId}/octopus-tokens/product-categories`, {
        params,
      });
      const list = Array.isArray(res.data) ? res.data : [];
      setCategories(list);
      setTotal(list.length);
    } catch (err: any) {
      setCategories([]);
      setTotal(undefined);
    } finally {
      setLoading(false);
    }
  };

  const searchedCategories = useMemo(() => {
    if (!searchText.trim()) return categories;
    const q = normalize(searchText);
    return categories.filter(
      (c) => normalize(c.Name).includes(q) || normalize(c.Warehouse?.Name || "").includes(q)
    );
  }, [categories, searchText]);

  const warehouseFilters = useMemo(() => {
    const names = Array.from(
      new Set(categories.map((c) => c.Warehouse?.Name).filter(Boolean) as string[])
    );
    return names.sort((a, b) => a.localeCompare(b, "sr")).map((n) => ({ text: n, value: n }));
  }, [categories]);

  const warehouseOptions = useMemo(
    () => warehouseFilters.map((f) => ({ label: f.text, value: f.value })),
    [warehouseFilters]
  );

  const setColumnFilter = (key: string, value: any[]) => {
    setFilteredInfo((prev) => ({ ...prev, [key]: value.length ? value : null }));
  };

  const displayedCategories = useMemo(() => {
    let result = searchedCategories;
    const warehouseVals: string[] | null = filteredInfo.Warehouse || null;
    if (warehouseVals?.length)
      result = result.filter((c) => warehouseVals.includes(c.Warehouse?.Name || ""));
    const statusVals: string[] | null = filteredInfo.IsActive || null;
    if (statusVals?.length)
      result = result.filter((c) => statusVals.includes(c.IsActive ? "active" : "inactive"));
    const forSaleVals: string[] | null = filteredInfo.IsForSale || null;
    if (forSaleVals?.length)
      result = result.filter((c) => forSaleVals.includes(c.IsForSale ? "yes" : "no"));
    return result;
  }, [searchedCategories, filteredInfo]);

  const activeCount = displayedCategories.filter((c) => c.IsActive).length;
  const forSaleCount = displayedCategories.filter((c) => c.IsForSale).length;
  const withWarehouseCount = displayedCategories.filter((c) => c.Warehouse?.Name).length;

  const hasActiveFilters =
    Object.values(filteredInfo).some((v) => (Array.isArray(v) ? v.length > 0 : v != null)) ||
    !!searchText.trim();

  if (!canView) {
    return (
      <Result
        status="403"
        title="403"
        subTitle={t("You don't have permission to view this page.")}
      />
    );
  }

  const columns = [
    {
      title: "Kategorija",
      dataIndex: "Name",
      key: "Name",
      sorter: (a: ProductCategory, b: ProductCategory) => a.Name.localeCompare(b.Name, "sr"),
      sortOrder: sortedInfo.columnKey === "Name" ? sortedInfo.order : null,
      render: (name: string, row: ProductCategory) => {
        const bg = asCssColor(row.BackColor);
        const fg = asCssColor(row.ForeColor);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                minWidth: 90,
                padding: "4px 10px",
                borderRadius: 6,
                textAlign: "center",
                fontSize: 12,
                fontWeight: 600,
                background: bg || "#f3f4f6",
                color: fg || "#374151",
                border: bg ? "none" : "1px solid #e5e7eb",
              }}
            >
              {name}
            </div>
            <span style={{ fontSize: 11, color: "#9ca3af" }}>#{row.Id}</span>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "IsActive",
      key: "IsActive",
      sorter: (a: ProductCategory, b: ProductCategory) => Number(a.IsActive) - Number(b.IsActive),
      sortOrder: sortedInfo.columnKey === "IsActive" ? sortedInfo.order : null,
      filters: [
        { text: "Aktivna", value: "active" },
        { text: "Neaktivna", value: "inactive" },
      ],
      filteredValue: filteredInfo.IsActive || null,
      onFilter: (value: any, record: ProductCategory) =>
        value === "active" ? record.IsActive : !record.IsActive,
      render: (_: boolean, row: ProductCategory) => (
        <Tag color={row.IsActive ? "green" : "default"}>
          {row.IsActive ? "Aktivna" : "Neaktivna"}
        </Tag>
      ),
    },
    {
      title: "Za prodaju",
      dataIndex: "IsForSale",
      key: "IsForSale",
      sorter: (a: ProductCategory, b: ProductCategory) => Number(a.IsForSale) - Number(b.IsForSale),
      sortOrder: sortedInfo.columnKey === "IsForSale" ? sortedInfo.order : null,
      filters: [
        { text: "Da", value: "yes" },
        { text: "Ne", value: "no" },
      ],
      filteredValue: filteredInfo.IsForSale || null,
      onFilter: (value: any, record: ProductCategory) =>
        value === "yes" ? record.IsForSale : !record.IsForSale,
      render: (v: boolean) => <Tag color={v ? "blue" : "default"}>{v ? "Da" : "Ne"}</Tag>,
    },
    {
      title: "Magacin",
      dataIndex: "Warehouse",
      key: "Warehouse",
      sorter: (a: ProductCategory, b: ProductCategory) =>
        (a.Warehouse?.Name || "").localeCompare(b.Warehouse?.Name || "", "sr"),
      sortOrder: sortedInfo.columnKey === "Warehouse" ? sortedInfo.order : null,
      filters: warehouseFilters,
      filteredValue: filteredInfo.Warehouse || null,
      filterSearch: true,
      onFilter: (value: any, record: ProductCategory) => record.Warehouse?.Name === value,
      render: (w: Warehouse | undefined) =>
        w?.Name ? (
          <span>
            {w.Name}
            {!w.IsActive && (
              <Tag color="default" style={{ marginLeft: 6 }}>
                neaktivan
              </Tag>
            )}
          </span>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <div style={{ padding: "24px 24px 0" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" }}>
          Kategorije proizvoda
        </h1>
      </div>

      {/* Search filters */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
          flexWrap: "wrap",
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: "12px 16px",
        }}
      >
        <Select
          placeholder="Izaberite lokaciju"
          value={selectedTokenId}
          onChange={setSelectedTokenId}
          loading={tokensLoading}
          style={{ minWidth: 220 }}
          optionLabelProp="label"
          options={tokens.map((tk) => ({
            value: tk._id,
            label: tk.name,
            desc: tk.locationId?.name,
          }))}
          optionRender={(opt) => (
            <div>
              <div style={{ fontWeight: 600 }}>{opt.data.label}</div>
              {opt.data.desc && (
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{opt.data.desc}</div>
              )}
            </div>
          )}
          notFoundContent={
            tokensLoading
              ? "Učitavanje..."
              : "Nema podešenih lokacija. Dodajte ih u Podešavanja → Octopus."
          }
        />

        <Input
          placeholder="Pretraga po nazivu kategorije..."
          prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          allowClear
          style={{ minWidth: 220 }}
          onPressEnter={handleSearch}
        />

        <Space size={4}>
          <span style={{ fontSize: 12, color: "#6b7280" }}>Aktivna:</span>
          <Select
            value={activeFilter}
            onChange={setActiveFilter}
            options={THREE_STATE_OPTIONS}
            style={{ width: 90 }}
          />
        </Space>

        <Space size={4}>
          <span style={{ fontSize: 12, color: "#6b7280" }}>Za prodaju:</span>
          <Select
            value={forSaleFilter}
            onChange={setForSaleFilter}
            options={THREE_STATE_OPTIONS}
            style={{ width: 90 }}
          />
        </Space>

        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={loading}
          disabled={!selectedTokenId}
        >
          Pretraži
        </Button>

        <Button
          icon={<AppstoreOutlined />}
          onClick={handleShowAll}
          loading={loading}
          disabled={!selectedTokenId}
        >
          Prikaži sve
        </Button>
      </div>

      {/* Advanced filters — extracted directly from the loaded categories */}
      {categories.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          <Input
            placeholder="Pretraži po nazivu kategorije ili magacina..."
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ minWidth: 280 }}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Filtriraj po magacinu"
            value={filteredInfo.Warehouse || []}
            onChange={(vals) => setColumnFilter("Warehouse", vals)}
            options={warehouseOptions}
            maxTagCount="responsive"
            style={{ minWidth: 220 }}
          />

          {hasActiveFilters && <Button onClick={resetTable}>Resetuj filtere</Button>}
        </div>
      )}

      {/* Summary row */}
      {searched && !loading && displayedCategories.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 24,
            marginBottom: 16,
            flexWrap: "wrap",
            fontSize: 13,
            color: "#6b7280",
          }}
        >
          <span>
            Ukupno kategorija:{" "}
            <strong style={{ color: "#111827" }}>{displayedCategories.length}</strong>
            {total != null && total !== categories.length && (
              <span style={{ color: "#9ca3af" }}> (od {total} na serveru)</span>
            )}
          </span>
          <span>
            Aktivnih: <strong style={{ color: "#111827" }}>{activeCount}</strong>
          </span>
          <span>
            Za prodaju: <strong style={{ color: "#111827" }}>{forSaleCount}</strong>
          </span>
          <span>
            Sa magacinom: <strong style={{ color: "#111827" }}>{withWarehouseCount}</strong>
          </span>
        </div>
      )}

      <Table
        dataSource={displayedCategories}
        columns={columns}
        rowKey="Id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        onChange={(_pagination, filters, sorter) => {
          setFilteredInfo(filters);
          setSortedInfo(Array.isArray(sorter) ? sorter[0] || {} : sorter);
        }}
        expandable={{
          expandedRowRender: (row) => (
            <div style={{ padding: "8px 16px", fontSize: 12, color: "#374151" }}>
              <Space size={24} wrap>
                <span>
                  ID kategorije: <strong>{row.Id}</strong>
                </span>
                {row.WarehouseId != null && (
                  <span>
                    ID magacina: <strong>{row.WarehouseId}</strong>
                  </span>
                )}
                {row.ButtonBackColor != null && (
                  <span>
                    ButtonBackColor: <code>{row.ButtonBackColor}</code>
                  </span>
                )}
                {row.ButtonForeColor != null && (
                  <span>
                    ButtonForeColor: <code>{row.ButtonForeColor}</code>
                  </span>
                )}
                {row.BackColor && (
                  <span>
                    BackColor: <code>{row.BackColor}</code>
                  </span>
                )}
                {row.ForeColor && (
                  <span>
                    ForeColor: <code>{row.ForeColor}</code>
                  </span>
                )}
              </Space>
            </div>
          ),
        }}
        locale={{
          emptyText: searched
            ? "Nema kategorija za izabrane filtere"
            : "Izaberite lokaciju i kliknite Pretraži",
        }}
        scroll={{ x: 700 }}
      />
    </div>
  );
};

export default OctopusCategories;
